import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const STALE_TIME = 30_000;
const EMPTY = [];

const patchListing = (id, patch) => (l) => (l._id === id ? { ...l, ...patch } : l);

export function useProperties({ search = '' } = {}) {
  const params = { search: search || undefined };
  const query = useQuery({
    queryKey: queryKeys.properties.list(params),
    queryFn: () => portalApi.listings(params),
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME
  });
  return {
    rows: query.data?.listings || EMPTY,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error
  };
}

export function usePropertyQuery(id) {
  return useQuery({
    queryKey: queryKeys.properties.detail(id),
    queryFn: () => portalApi.listing(id),
    enabled: Boolean(id),
    staleTime: STALE_TIME
  });
}

export function useCreateProperty() {
  return useApiMutation({
    mutationFn: (values) => portalApi.createListing(values),
    invalidate: [queryKeys.properties.lists()],
    success: (data) => `${data.listing.name} added`,
    error: 'Could not add this property'
  });
}

export function useUpdateProperty() {
  return useApiMutation({
    mutationFn: ({ _id, ...values }) => portalApi.updateListing(_id, values),
    optimistic: ({ _id, ...values }) => [
      { key: queryKeys.properties.lists(), update: (data) => ({ ...data, listings: data.listings.map(patchListing(_id, values)) }) },
      { key: queryKeys.properties.detail(_id), update: (data) => ({ ...data, listing: { ...data.listing, ...values } }) }
    ],
    invalidate: [queryKeys.properties.all],
    success: 'Property updated',
    error: 'Could not update this property'
  });
}

export function useDeleteProperty() {
  return useApiMutation({
    mutationFn: (id) => portalApi.removeListing(id),
    optimistic: (id) => [
      { key: queryKeys.properties.lists(), update: (data) => ({ ...data, listings: data.listings.filter((l) => l._id !== id) }) }
    ],
    invalidate: [queryKeys.properties.lists()],
    success: 'Property removed',
    error: 'Could not remove this property'
  });
}

export function useAddPropertyNote(id) {
  return useApiMutation({
    mutationFn: (text) => portalApi.addListingNote(id, text),
    optimistic: (text) => [
      {
        key: queryKeys.properties.detail(id),
        update: (data) => ({
          ...data,
          listing: {
            ...data.listing,
            noteThread: [...(data.listing.noteThread || []), { _id: 'pending', text, by: 'You', createdAt: new Date().toISOString() }]
          }
        })
      }
    ],
    invalidate: [queryKeys.properties.detail(id), queryKeys.properties.lists()],
    error: 'Could not save this note'
  });
}
